import type { ColumnSketch } from '@/shared/api/generated/catalog';

/* What a profile can tell a reader about each quality dimension, before any rule exists.
 *
 * **Measure, do not score — and that distinction is the whole file.** `profileBaseline` deliberately
 * deducts for nothing but completeness and cardinality shape, because a deduction is a claim that
 * something is *wrong* and a profile cannot know intent: a `store_sk` at 0.3 uniqueness is a correct
 * dimension column, not a 70% failure. But the *measurement* is real, it is already on the wire, and
 * withholding it leaves a reader with no way to decide which rules to write — which is exactly the
 * job the profile is supposed to do first.
 *
 * So each dimension gets one of three honest answers:
 *
 * * **`measured`** — the profile computed it. Shown as an observation with no verdict attached.
 * * **`needsRule`** — computable only against a declared expectation, which is a rule. The reason
 *   names what the expectation would be, so "write a rule" is actionable rather than a shrug.
 * * **`needsReference`** — not computable from this table at all, at any effort.
 *
 * **Why `needsRule` is not a failure state.** A screen that greyed these out would read as "the
 * platform cannot do this"; what is true is that nobody has said what *correct* means yet. The
 * distinction matters because the two lead somewhere different: one is a gap in this platform, the
 * other is a gap in the customer's declared expectations, and only the second is the reader's to
 * close.
 *
 * The six dimensions are DAMA's, matching `scoring`'s own model, so a reader who later sees a rule
 * score is looking at the same vocabulary rather than a second one invented here.
 */

export const DIMENSIONS = [
  'completeness',
  'uniqueness',
  'validity',
  'consistency',
  'timeliness',
  'accuracy',
] as const;

export type Dimension = (typeof DIMENSIONS)[number];

export type Availability = 'measured' | 'needsRule' | 'needsReference';

export interface DimensionReading {
  readonly dimension: Dimension;
  readonly availability: Availability;
  /** The measured figure as a 0–1 ratio, or `null` when there is nothing measured.
   *
   * `null` for every non-`measured` dimension, and also for a `measured` one the profile could not
   * evaluate — an empty relation measures neither completeness nor uniqueness, and reporting 0 for
   * either would say the table is entirely null and entirely duplicated. Absence is not zero. */
  readonly value: number | null;
  /** How many columns the figure rests on. `0` where nothing was measurable. */
  readonly columns: number;
}

/** Below this share of distinct values a column is worth a reader's eye — not a failure.
 *
 * Named rather than inlined so the screen's wording and this threshold cannot drift apart. It is a
 * *notability* line, deliberately not a pass mark: nothing in this file passes or fails anything.
 */
export const LOW_UNIQUENESS = 0.1;

/** The average non-null share across columns the profile could measure.
 *
 * Averaged per column rather than weighted by rows, because every column was scanned over the same
 * relation — so the row count is common to all of them and weighting would change nothing except to
 * make the figure harder to explain.
 */
function completenessOf(columns: readonly ColumnSketch[]): DimensionReading {
  const measured = columns.filter((column) => column.completeness !== null);
  if (measured.length === 0) {
    return {
      dimension: 'completeness',
      availability: 'measured',
      value: null,
      columns: 0,
    };
  }
  const total = measured.reduce((sum, column) => sum + (column.completeness ?? 0), 0);
  return {
    dimension: 'completeness',
    availability: 'measured',
    value: total / measured.length,
    columns: measured.length,
  };
}

/** The average distinct-to-rows ratio across columns with rows to divide by.
 *
 * **Measured and never scored.** A low ratio is the normal shape of a dimension column and the
 * expected shape of a flag; it is a finding only against a column somebody has declared a key, and
 * declaring one is a `unique` rule. So this reports the number and says nothing about it.
 *
 * `distinct_exact` in preference to the estimate, because the estimator carries a ~3% bar and this
 * figure is read as a plain fact. A column whose `rows_scanned` is zero is excluded rather than
 * counted as 0 or 1 — an empty relation has no uniqueness either way.
 */
function uniquenessOf(columns: readonly ColumnSketch[]): DimensionReading {
  const measured = columns.filter((column) => column.rows_scanned > 0);
  if (measured.length === 0) {
    return { dimension: 'uniqueness', availability: 'measured', value: null, columns: 0 };
  }
  const total = measured.reduce((sum, column) => {
    const distinct = column.distinct_exact ?? column.distinct_estimate;
    /* Clamped at 1: a distinct *estimate* can exceed the row count by its own error bar, and a
       ratio above 1 would render as "108% unique", which is not a thing. */
    return sum + Math.min(1, distinct / column.rows_scanned);
  }, 0);
  return {
    dimension: 'uniqueness',
    availability: 'measured',
    value: total / measured.length,
    columns: measured.length,
  };
}

/** Every dimension, in DAMA order, with the honest answer for each.
 *
 * Total over `DIMENSIONS` by construction — the array is mapped rather than a list being maintained
 * beside it, so a seventh dimension cannot be added and silently omitted from the readout.
 */
export function dimensionReadout(
  columns: readonly ColumnSketch[],
): readonly DimensionReading[] {
  return DIMENSIONS.map((dimension) => {
    switch (dimension) {
      case 'completeness':
        return completenessOf(columns);
      case 'uniqueness':
        return uniquenessOf(columns);
      case 'validity':
        /* Needs a *pattern* — "what does a valid PAN look like" is an expectation, and a profile
           has only the values. The frequent-values summary hints at shape and cannot judge it. */
        return { dimension, availability: 'needsRule', value: null, columns: 0 };
      case 'consistency':
        /* Needs a second thing to be consistent *with*: another column, another table, or a
           declared relationship. A single column cannot be inconsistent on its own. */
        return { dimension, availability: 'needsRule', value: null, columns: 0 };
      case 'timeliness':
        /* The profile sees the values in a timestamp column and not the cadence they should arrive
           at. "Four days old" is a fact; whether that is late is a threshold somebody sets, which is
           what a freshness rule is. Deliberately `needsRule` rather than `measured` with a figure:
           the sketch does not carry a max timestamp, so there is no number to show, and inventing
           one from the frequent-values summary would be a guess dressed as a measurement. */
        return { dimension, availability: 'needsRule', value: null, columns: 0 };
      case 'accuracy':
        /* Not computable from this table at any effort: accuracy is agreement with an authority
           outside it. Reconciliation is that mechanism, and it takes two sources. */
        return { dimension, availability: 'needsReference', value: null, columns: 0 };
    }
  });
}

/** The copy key for a dimension's explanation, so the wording lives in the catalogue. */
export function dimensionReasonKey(reading: DimensionReading): string {
  return `profile.dimension.${reading.dimension}.${reading.availability}`;
}
